import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { RecommendationPreferenceMode } from '@optiflow/types';
import { cn } from '@/utils/cn';

/** A labelled group of filter controls. */
export function FilterSection({
  legend,
  hint,
  children,
}: {
  legend: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="border-line min-w-0 space-y-3 border-b pb-5 last:border-b-0 last:pb-0">
      <legend className="text-ink mb-3 text-sm font-semibold">{legend}</legend>
      {hint ? <p className="text-muted -mt-1 text-xs">{hint}</p> : null}
      {children}
    </fieldset>
  );
}

/** Toggle buttons for choosing any number of values from a fixed list. */
export function ChoiceChips<T extends string>({
  choices,
  selected,
  onChange,
  labelFor,
  label,
}: {
  choices: readonly T[];
  selected: readonly T[];
  onChange: (next: T[]) => void;
  labelFor: (choice: T) => string;
  /** Names the choices as a group of their own; leave it out inside a FilterSection, whose legend already does. */
  label?: string;
}) {
  const toggle = (choice: T) =>
    onChange(
      selected.includes(choice)
        ? selected.filter((value) => value !== choice)
        : [...selected, choice],
    );

  return (
    <div role={label ? 'group' : undefined} aria-label={label} className="flex flex-wrap gap-2">
      {choices.map((choice) => {
        const isSelected = selected.includes(choice);
        return (
          <button
            key={choice}
            type="button"
            aria-pressed={isSelected}
            onClick={() => toggle(choice)}
            className={cn(
              'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-sm transition-colors',
              isSelected
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-line text-ink hover:border-brand-300 bg-white',
            )}
          >
            {isSelected ? <Check className="size-3.5" aria-hidden /> : null}
            {labelFor(choice)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Whether a choice ranks frames or keeps only the matching ones.
 *
 * The API's words are PREFER and ONLY, or RANK and ONLY for sizes; the optician reads "Prefer" and "Only show", with a line saying what the choice does.
 */
export function PreferenceModeChoice<T extends string>({
  value,
  onChange,
  preferValue,
  onlyValue,
  preferHint,
  onlyHint,
  label,
}: {
  value: T;
  onChange: (next: T) => void;
  preferValue: T;
  onlyValue: T;
  preferHint: string;
  onlyHint: string;
  label: string;
}) {
  const { t } = useTranslation();
  const name = useId();

  const option = (optionValue: T, text: string) => (
    // Relative, so the visually hidden radio is positioned inside its label; otherwise it escapes the drawer's scrolling area and makes the whole dialog scroll.
    <label
      className={cn(
        'relative flex min-h-9 flex-1 cursor-pointer items-center justify-center rounded-md px-3 text-sm font-medium transition-colors',
        'has-[:focus-visible]:ring-brand-500 has-[:focus-visible]:ring-2',
        value === optionValue ? 'text-ink bg-white shadow-sm' : 'text-muted hover:text-ink',
      )}
    >
      <input
        type="radio"
        name={name}
        checked={value === optionValue}
        onChange={() => onChange(optionValue)}
        className="sr-only"
      />
      {text}
    </label>
  );

  return (
    <div className="space-y-1.5">
      <div
        role="radiogroup"
        aria-label={label}
        className="bg-canvas border-line flex gap-1 rounded-lg border p-1"
      >
        {option(preferValue, t('frameRecommendations.filters.prefer'))}
        {option(onlyValue, t('frameRecommendations.filters.only'))}
      </div>
      <p className="text-muted text-xs">{value === onlyValue ? onlyHint : preferHint}</p>
    </div>
  );
}

export const PREFER = RecommendationPreferenceMode.PREFER;
export const ONLY = RecommendationPreferenceMode.ONLY;
